<template>
  <Teleport to="body">
    <Transition
      name="backdrop"
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        aria-hidden="true"
        @click="handleClose"
      ></div>
    </Transition>

    <Transition
      :enter-from-class="
        props.pageLang === 'ar' ? 'translate-x-full' : 'translate-x-full'
      "
      :leave-to-class="
        props.pageLang === 'ar' ? 'translate-x-full' : 'translate-x-full'
      "
      enter-active-class="transition-transform duration-300 ease-out"
      leave-active-class="transition-transform duration-200 ease-in"
      enter-to-class="translate-x-0"
      leave-from-class="translate-x-0"
    >
      <aside
        v-if="isOpen"
        class="fixed end-0 top-0 z-50 h-full w-full max-w-md overflow-auto bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="panel-title"
        @click.stop
      >
        <!-- Header -->
        <header
          class="flex items-center justify-between border-b border-gray-200 px-6 py-4 md:mt-0 mt-12"
        >
          <h2 id="panel-title" class="text-lg font-semibold text-gray-900">
            Notes
          </h2>
          <button
            type="button"
            class="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            aria-label="Close panel"
            @click="handleClose"
          >
            <X class="h-5 w-5" />
          </button>
        </header>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div
            v-if="!notes?.items?.length"
            class="flex flex-col items-center justify-center py-12 text-center"
          >
            <div class="rounded-full bg-gray-100 p-3 mb-4">
              <FileText class="h-6 w-6 text-gray-400" />
            </div>
            <p class="text-sm text-gray-500">No notes available</p>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="(item, index) in notes.items"
              :key="index"
              class="rounded-lg border border-gray-200 bg-gray-50 p-4 transition-colors hover:bg-gray-100"
            >
              <!-- Simple string note -->
              <p
                v-if="typeof item === 'string'"
                class="text-sm text-gray-700 leading-relaxed"
              >
                {{ item }}
              </p>

              <!-- Complex note with title and subitems -->
              <div v-else class="space-y-2">
                <h3 class="font-medium text-gray-900 text-sm">
                  {{ item.title }}
                </h3>
                <ul class="space-y-1 pl-3">
                  <li
                    v-for="(sub, subIndex) in item.items"
                    :key="subIndex"
                    class="text-sm text-gray-600 leading-relaxed relative before:content-['•'] before:absolute before:-left-3 before:text-gray-400"
                  >
                    {{ sub }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <footer class="border-t border-gray-200 px-6 py-4">
          <p class="text-xs text-gray-500 text-center">
            {{ notes?.items?.length || 0 }} note{{
              (notes?.items?.length || 0) !== 1 ? 's' : ''
            }}
          </p>
        </footer>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { X, FileText } from 'lucide-vue-next'
import { onMounted, onUnmounted } from 'vue'

// Types
interface NoteItem {
  title: string
  items: string[]
}

interface NotesData {
  items: (string | NoteItem)[]
}

interface Props {
  isOpen: boolean
  notes?: NotesData
  pageLang: string
  pageDir?: string
}

// Props and emits
const props = withDefaults(defineProps<Props>(), {
  notes: () => ({ items: [] }),
  pageLang: 'en',
  pageDir: 'rtl'
})

const emit = defineEmits<{
  closeSidePanel: []
}>()

const handleClose = () => {
  emit('closeSidePanel')
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isOpen) {
    handleClose()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
